import {
    useCallback,
    useState,
} from 'react';
import { useIntl } from 'react-intl';

import { notificationType } from '../../../../common/constants/Constant';
import { useUiSnackBar } from '../../../../common/hooks/useUiSnackBar';
import type {
    CodedDashboard,
    EditCodedDashboardPayload,
} from '../../../../services/governanceV2/CodedDashboardsService';
import { editCodedDashboard } from '../../../../services/governanceV2/CodedDashboardsService';
import { useTenantsContext } from '../../../tenants/TenantsContextProvider';
import { useCodedDashboardsContext } from '../dashboards/hooks/useCodedDashboards';
import type { PinSource } from '../defaultPage/templateDeploy/types';
import { trackPinToggled } from '../defaultPage/util/dashboardTemplatesTelemetry';
import { DASHBOARD_TAGS } from '../util/constants';

interface TogglePinnedDashboard {
    /** An id, not a boolean: one caller is a list and must mark only the row being written. */
    pendingId: string | null;
    /** True once the write landed and the shared listing was re-read. */
    togglePin: (dashboard: CodedDashboard, shouldPin: boolean) => Promise<boolean>;
}

/**
 * Pins or unpins a deployed dashboard — just a tag on the app, which the left nav filters on.
 *
 * Shared because the platform's edit endpoint *replaces* the record rather than patching it: every
 * field has to be resent or it is blanked, and two copies of that rule is how a field gets wiped.
 *
 * Refreshes the shared listing on success. Callers with their own query refresh off the return value.
 */
export function useTogglePinnedDashboard(source: PinSource): TogglePinnedDashboard {
    const { formatMessage: translate } = useIntl();
    const { refresh } = useCodedDashboardsContext();
    // Must match the listing's resolution: `selectedTenant.id` is empty on a page opened without a
    // hydrated tenant, where the listing still works via the fallback — so it would fail only on write.
    const { selectedTenant } = useTenantsContext();
    const tenantId = selectedTenant.id;
    const { createNotificationWithOptions } = useUiSnackBar();

    const [ pendingId, setPendingId ] = useState<string | null>(null);

    const togglePin = useCallback(async (dashboard: CodedDashboard, shouldPin: boolean) => {
        setPendingId(dashboard.id);

        try {
            const tags = shouldPin
                ? [ ...dashboard.tags, DASHBOARD_TAGS.PINNED ]
                : dashboard.tags.filter(tag => tag !== DASHBOARD_TAGS.PINNED);

            const payload: EditCodedDashboardPayload = {
                version: dashboard.deployVersion,
                description: dashboard.deploymentDescription,
                title: dashboard.deploymentTitle,
                tags,
            };

            await editCodedDashboard(tenantId, dashboard.deploymentFolder.key, dashboard.id, payload);
            await refresh();
            trackPinToggled({
                source,
                pinned: shouldPin,
                succeeded: true,
            });

            return true;
        } catch {
            trackPinToggled({
                source,
                pinned: shouldPin,
                succeeded: false,
            });
            createNotificationWithOptions({
                message: translate({ id: 'CLIENT_GOVERNANCE_V2_NAV_PIN_FAILED' }),
                type: notificationType.ERROR,
            });

            return false;
        } finally {
            setPendingId(null);
        }
    }, [ createNotificationWithOptions, refresh, source, tenantId, translate ]);

    return {
        pendingId,
        togglePin,
    };
}
